// @flow

import React, { useEffect, useRef, useState } from 'react'
import { LANGUAGES } from '../utils/translations'
import '../styles/LanguageSelector.scss'

type Props = {
  activeLanguages: Array<string>,
  lang: string,
  onChange: (code: string) => void,
  t: Function,
}

/**
 * Sélecteur de langue en liste déroulante (drapeau + libellé).
 * Masqué si le restaurant n'a activé qu'une seule langue.
 */
const LanguageSelector = ({ activeLanguages, lang, onChange, t }: Props) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('touchstart', onClickOutside)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('touchstart', onClickOutside)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!activeLanguages || activeLanguages.length < 2) return null

  const options = LANGUAGES.filter((l) => activeLanguages.includes(l.code))
  const current = options.find((l) => l.code === lang) || options[0]

  const select = (code) => {
    setOpen(false)
    if (code !== lang) onChange(code)
  }

  return (
    <div className={`lang-select ${open ? 'open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="lang-select-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('modalFlag.selectLanguage')}
        onClick={() => setOpen(!open)}
      >
        <span className="lang-select-flag" role="img" aria-hidden="true">{current.flag}</span>
        <span className="lang-select-label">{current.label}</span>
        <svg className="lang-select-chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5.5 7.5L10 12l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="lang-select-menu" role="listbox" aria-label={t('modalFlag.selectLanguage')}>
          {options.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === current.code}>
              <button
                type="button"
                className={`lang-select-option ${l.code === current.code ? 'selected' : ''}`}
                onClick={() => select(l.code)}
              >
                <span className="lang-select-flag" role="img" aria-hidden="true">{l.flag}</span>
                <span className="lang-select-label">{l.label}</span>
                {l.code === current.code && (
                  <svg className="lang-select-check" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5 10.5l3.5 3.5L15 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default LanguageSelector
